import { Card, CardContent, Autocomplete } from '../../../components/ui';
import Button from '../../../components/ui/Button';
import type { StoreBrandSearchResult } from '../../../types/api.types';

interface BrandSearchSectionProps {
    value: StoreBrandSearchResult | null;
    onSelect: (brand: StoreBrandSearchResult | null) => void;
    onSearch: (searchText: string) => Promise<StoreBrandSearchResult[]>;
    onOpenBrandSettings: () => void;
    onOpenAllUsersEvent: () => void;
}

export default function BrandSearchSection({
    value,
    onSelect,
    onSearch,
    onOpenBrandSettings,
    onOpenAllUsersEvent,
}: BrandSearchSectionProps) {
    return (
        <Card>
            <CardContent className="py-6 space-y-4">
                <Autocomplete<StoreBrandSearchResult>
                    label="Search store brand"
                    placeholder="Type at least 3 characters to search..."
                    value={value}
                    onSelect={onSelect}
                    onSearch={onSearch}
                    displayField="name"
                    idField="_id"
                    minSearchLength={3}
                />

                {value && (
                    <div className="pt-4 border-t border-[#e2e8f0] space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-[#ede9fe] flex items-center justify-center flex-shrink-0">
                                <svg
                                    className="w-4 h-4 text-[#6e64f0]"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"
                                    />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs font-medium text-[#64748b] uppercase tracking-wide">
                                    Selected brand
                                </p>
                                <p className="text-sm font-semibold text-[#1e293b] truncate">
                                    {value.name}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <Button variant="secondary" onClick={onOpenBrandSettings}>
                                <svg
                                    className="w-4 h-4 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                    />
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                </svg>
                                Brand settings
                            </Button>
                            <Button variant="primary" onClick={onOpenAllUsersEvent}>
                                <svg
                                    className="w-4 h-4 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                    />
                                </svg>
                                Update all stores
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
